import {InventoryRequest} from "../../../../api/Logistics";
import {StatusCol} from "../openings/Openings";
import {api} from "../../../../api/API";
import {CheckboxSelectorCol} from "../Checkbox";
import {actionsColName, AdjustCol, DateCol, Nullable, NumberCol, PrefixSearch, StringCol, Table, ViewOnly} from "../../../../misc/scroller/Table";
import moment from "moment/moment";
import {Tooltip} from "@mui/material";
import {logisticsTableName} from "./Logistics";
import {useProjectId} from "../ProjectName";
import {getAll} from "../../../logistics/routes/useGetAll";
import {PriceColumns, SourceCol} from "./Openings";
import {FakeLink} from "../../../../misc/FakeLink";
import {useState} from "react";
import {ChangeHistory} from "../openings/ChangeHistory";
import {useUser} from "../../../../misc/Permission";
import {DateFilterCol, WithDateFilter} from "../../../../misc/DateFilterForCol";
import {LogisticsView} from "./ViewButton";
import {releaseListParams, releasePrefName} from "./ReleaseColumns";
import {defaultDiv10Tab, Div10Tabs, TDiv10Type} from "../div10/Div10Tabs";
import {useQueryParam} from "../../quote/pricing/QueryParam";
import {useProjectUnitSystem} from "../ProjectName";
import {getColumns} from "../div10/Div10Table";
import {byFixedOrder} from "../pricing/Pricing";
import {RowActions} from "./RowActions";

const div10Groups = [
    'div-10/corner-guard', 'div-10/locker', 'div-10/mailbox', 'div-10/wr-accessory', 'div-10/wr-partition',
];

export function Div10(props: {
    view: LogisticsView;
    nonStockingForRelease?: number;
}) {
    const project = useProjectId();
    const sys = useProjectUnitSystem();
    const [tab2, setTab2] = useQueryParam<TDiv10Type>("tab2", defaultDiv10Tab);
    const isRelease = props.view === "release";
    const [showChangeHistoryForOpening, setShowChangeHistoryForOpening] = useState<{
        inventoryRequest?: {
            id: number;
            qty: number;
            status: string;
        }
    }>();

    const u = useUser();
    if(!u) return null;
    if(!sys) return null;

    const prefName = isRelease ? releasePrefName(project, "div10") : logisticsTableName(project, "div10");

    return (
        <>
            {showChangeHistoryForOpening && <ChangeHistory inventoryRequest={showChangeHistoryForOpening.inventoryRequest} onDone={() => setShowChangeHistoryForOpening(undefined)} />}
            {isRelease && <Div10Tabs tab={tab2} onChange={setTab2} />}
            <Table<InventoryRequest>
                key={isRelease ? "release-" + tab2 : "default"}
                name={logisticsTableName(project, "div10")}
                overridePrefsName={isRelease ? prefName : undefined}
                globalPrefsName={isRelease ? releasePrefName(0, "div10") : logisticsTableName(0, "div10")}
                fetch={ctx => getAll(ctx, offset => api.logistics.listInventoryRequest({
                    project: project,
                    offset: offset,
                    category: "div10",
                    nonStockingForRelease: props.nonStockingForRelease,
                    ...releaseListParams(props.view, "div10"),
                }))}
                fetchDeps={[project, props.nonStockingForRelease, props.view]}
                columnDeps={[u, props.view, tab2, sys]}
                groupBy={isRelease ? {
                    fixedGroups: div10Groups,
                    groupFx: row => "div-10/" + (row?.category || "wr-accessory"),
                    groupSorter: byFixedOrder(div10Groups),
                    groupFilter: (groupName: string) => groupName === "div-10/" + tab2,
                } : undefined}
                columns={isRelease ? getColumns(tab2, project, sys, true, prefName).map(v => ViewOnly(v as any)) : [
                    CheckboxSelectorCol(),
                    AdjustCol(ViewOnly(NumberCol("Inv Req ID", "id", 80)), {hiddenByDefault: true}),
                    {
                        name: actionsColName,
                        noCsv: true,
                        render: (o: InventoryRequest) => <RowActions row={o} type="div10" />,
                        renderName: () => null,
                        width: 40,
                    },
                    ViewOnly(StringCol("Room", "room", 100)),
                    ViewOnly(StringCol("Type", "category", 100)),
                    ViewOnly(StringCol("Product", "productCategoryName", 100)),
                    ViewOnly(StringCol("Manufacturer", "manufacturerName", 120)),
                    ViewOnly(PrefixSearch(StringCol("Code", "productCode", 100))),
                    ViewOnly(NumberCol("Qty", "qty", 50)),
                    StatusCol("Status",  "status"),
                    SourceCol(),
                    {
                        name: "Meta",
                        render: (data) => {

                            return (
                                <>
                                    <FakeLink onClick={() => setShowChangeHistoryForOpening({
                                        inventoryRequest: {
                                            id: data.id,
                                            status: data.status,
                                            qty: data.qty,
                                        }
                                    })}>
                                        {moment(data.updatedAt).format("MMM D")} by {data.updatedByName}
                                    </FakeLink>
                                    {data.followupRequest && <>
                                        <Tooltip title={`Expected to receive this entire order, but ${data.receivedQty} items came in. A follow up order request has been created.`}><div>*Warning</div></Tooltip>
                                    </>}
                                    {data.isWaste && <>
                                        <Tooltip title={`This item was marked as waste. See waste tab for more details`}><div>*Warning</div></Tooltip>
                                    </>}
                                </>
                            );
                        },
                        sort: (a, b) => {
                            const result = moment(a.updatedAt).unix() - moment(b.updatedAt).unix()
                            if(result === 0) return result;
                            if(result < 0) return -1;
                            return 1;
                        },
                        width: 120,
                    },
                    WithDateFilter<InventoryRequest>(Nullable(DateCol("Needed By", "neededBy", "MMMM D", 200)), "neededBy", "future"),
                    DateFilterCol<InventoryRequest>("Delivered", "deliveredAt", "MMM D, YYYY", 110),
                    ...PriceColumns(u)
                ]}

                onChange={input => api.logistics.updateInventoryRequest(input)}
            />
        </>
    )
}